import type { TreeNodeVersion } from '../types';
import type { ComponentProps } from 'react';

import React, { useCallback } from 'react';

import useClassy from '@core/hooks/useClassy';
import { InitializeSuperHubBranches, useSuperHubStore } from '@core/store';

import BranchStatusIndicator from '@routes/SuperHub/components/ProjectNavigation/BranchStatusIndicator';

import Badge from '@ui/Badge';
import Button from '@ui/Button';
import Icon from '@ui/Icon';
import Truncate from '@ui/Truncate';

import EditVersion from '../../EditVersion';
import useBuildBranchPath from '../../hooks/useBuildBranchPath';
import { useRedirectPendingModalContext } from '../../Modals';
import Tooltip from '../Tooltip';

import { getBranchItemId } from './Branch';
import styles from './Version.module.scss';

/** Default height of a version item in the virtual list. */
export const VERSION_ITEM_HEIGHT = 29;

interface VersionItemProps extends ComponentProps<'div'> {
  node: TreeNodeVersion;
}

export const VersionItem = React.memo(function VersionItem({ node, ...props }: VersionItemProps) {
  const bem = useClassy(styles, 'VersionItem');
  const buildVersionPath = useBuildBranchPath();
  const currentBranch = useSuperHubStore(s => s.currentBranch);
  const { setRedirectPendingModalValue } = useRedirectPendingModalContext();

  const { data: version } = node;
  const formattedVersionName = `v${version.name}`;
  const isLive = ['public', 'default'].includes(version.privacy.view);
  const isCurrent = version.name === currentBranch.name;
  const path = buildVersionPath(version.name);

  const handleLinkClick = useCallback(
    event => {
      if (isCurrent) {
        event.preventDefault();
        return;
      }

      // Show redirect modal while browser slowly navigates to this version.
      setRedirectPendingModalValue(`v${version.name}`);
    },
    [isCurrent, setRedirectPendingModalValue, version.name],
  );

  return (
    <InitializeSuperHubBranches revalidateOnFocus version={node.isOpen ? node.data.name : undefined}>
      <div
        {...props}
        aria-expanded={node.isOpen}
        aria-label={formattedVersionName}
        aria-level={node.nestingLevel + 1}
        aria-owns={node.branches.map(branch => getBranchItemId(branch)).join(' ') || undefined}
        aria-posinset={node.position + 1}
        aria-selected={isCurrent}
        aria-setsize={node.positionLength}
        className={bem('&', isCurrent && '_current', node.isOpen && '_opened')}
        role="treeitem"
      >
        <Tooltip asTitle content={node.isOpen ? 'Hide Branches' : 'Show Branches'}>
          <Button
            aria-label={node.isOpen ? 'Hide Branches' : 'Show Branches'}
            className={bem('-toggle')}
            kind="minimum"
            loading={node.isBranchesLoading}
            onClick={() => node.toggleOpen()}
            size="xs"
            text
          >
            <Icon aria-hidden="true" className={bem('-toggle-icon')} name="chevron-right" />
          </Button>
        </Tooltip>

        <a className={bem('-link')} href={path} onClick={handleLinkClick}>
          <Truncate>{formattedVersionName}</Truncate>
          {!!isLive && <BranchStatusIndicator />}
          {version.privacy.view === 'default' && (
            <Badge allCaps className={bem('-badge')} ghost kind="dark">
              Default
            </Badge>
          )}
          {version.state === 'deprecated' && (
            <Badge allCaps className={bem('-badge')} ghost kind="dark">
              Deprecated
            </Badge>
          )}
          {version.release_stage === 'beta' && (
            <Badge allCaps className={bem('-badge')} ghost kind="dark">
              Beta
            </Badge>
          )}
        </a>

        <Tooltip asTitle content="Coming soon!">
          <Button
            aria-label="Create Branch"
            className={bem('-create-button')}
            kind="contrast"
            size="xs"
            text
            uppercase={false}
          >
            <Icon aria-hidden="true" name="plus" />
            Branch
          </Button>
        </Tooltip>
        <EditVersion className={bem('-edit-button')} version={version} />
      </div>
    </InitializeSuperHubBranches>
  );
});
